/**
 * useNotificationChannels Hook
 *
 * Manages notification channel state and operations.
 * Extracted from app/routes/app.settings.tsx for better reusability.
 *
 * Features:
 * - Optimistic UI updates for instant feedback
 * - Automatic rollback on API errors
 * - Channel CRUD operations
 * - Email verification flow
 *
 * Uses the notification-channels service layer for API operations.
 * @see ../../services/notification-channels for API implementation
 */

import { useState, useEffect, useCallback } from 'react';
import * as notificationChannelsApi from '../../services/notification-channels';
import { NotificationChannelApiError } from '../../services/notification-channels';
import type { NotificationChannel } from '../../components/dashboard/NotificationChannels';

interface UseNotificationChannelsOptions {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

interface ChannelsByType {
  email: NotificationChannel[];
  slack: NotificationChannel[];
  webhook: NotificationChannel[];
  digest: NotificationChannel[];
}

interface UseNotificationChannelsReturn {
  // Channel data
  channels: NotificationChannel[];
  channelsByType: ChannelsByType;

  // Channel operations
  refreshChannels: () => Promise<void>;
  testChannel: (channel: NotificationChannel) => Promise<void>;
  toggleChannel: (channel: NotificationChannel) => Promise<void>;
  deleteChannel: (channel: NotificationChannel) => Promise<void>;
  deleteRecipient: (emailAddress: string) => void;

  // Verification operations
  sendVerification: (channel: NotificationChannel) => Promise<void>;
  verifyCode: (channel: NotificationChannel, code: string) => Promise<void>;

  // Add operations
  addEmailChannel: (name: string, email: string) => Promise<void>;
  addDigestChannel: (name: string, email: string) => Promise<void>;
  addSlackChannel: (name: string, webhookUrl: string) => Promise<void>;
  addWebhookChannel: (name: string, url: string, customHeaders?: Record<string, string>) => Promise<void>;
}

/**
 * Hook for managing notification channels
 */
export function useNotificationChannels(
  initialChannels: NotificationChannel[],
  options: UseNotificationChannelsOptions = {}
): UseNotificationChannelsReturn {
  const { onSuccess, onError } = options;
  const [channels, setChannels] = useState<NotificationChannel[]>(initialChannels);

  // Sync with loader data changes
  useEffect(() => {
    setChannels(initialChannels);
  }, [initialChannels]);

  // Group channels by type
  const channelsByType: ChannelsByType = {
    email: channels.filter((c) => c.channel_type === 'email' && c.config?.email_type !== 'digest'),
    slack: channels.filter((c) => c.channel_type === 'slack'),
    webhook: channels.filter((c) => c.channel_type === 'webhook'),
    digest: channels.filter((c) => c.channel_type === 'email' && c.config?.email_type === 'digest'),
  };

  // Refresh channels from API
  const refreshChannels = useCallback(async () => {
    try {
      const fetchedChannels = await notificationChannelsApi.fetchChannels();
      setChannels(fetchedChannels);
    } catch {
      // Silent fail for refresh
    }
  }, []);

  // Test channel
  const testChannel = useCallback(
    async (channel: NotificationChannel) => {
      try {
        await notificationChannelsApi.testChannel(channel.id);
        onSuccess?.(`Test notification sent to ${channel.channel_name}`);
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to send test notification');
      }
    },
    [onSuccess, onError]
  );

  // Toggle channel enabled state
  const toggleChannel = useCallback(
    async (channel: NotificationChannel) => {
      const newEnabled = !channel.enabled;

      // Optimistic update
      setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, enabled: newEnabled } : c)));
      onSuccess?.(`${channel.channel_name} ${newEnabled ? 'enabled' : 'disabled'}`);

      try {
        await notificationChannelsApi.updateChannel({ channelId: channel.id, enabled: newEnabled });
      } catch (error) {
        // Rollback on failure
        setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, enabled: !newEnabled } : c)));
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to update channel');
      }
    },
    [onSuccess, onError]
  );

  // Delete channel
  const deleteChannel = useCallback(
    async (channel: NotificationChannel) => {
      const originalChannels = channels;

      // Optimistic update
      setChannels((prev) => prev.filter((c) => c.id !== channel.id));
      onSuccess?.(`${channel.channel_name} deleted`);

      try {
        await notificationChannelsApi.deleteChannel(channel.id);
      } catch (error) {
        // Rollback on error
        setChannels(originalChannels);
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to delete channel');
      }
    },
    [channels, onSuccess, onError]
  );

  // Delete recipient by email (local state update only)
  const deleteRecipient = useCallback((emailAddress: string) => {
    setChannels((prev) => prev.filter((c) => c.channel_type !== 'email' || c.config?.email_address !== emailAddress));
  }, []);

  // Send verification code
  const sendVerification = useCallback(
    async (channel: NotificationChannel) => {
      try {
        await notificationChannelsApi.sendVerification(channel.id);
        onSuccess?.(`Verification code sent to ${channel.config?.email_address || channel.channel_name}`);
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to send verification code');
      }
    },
    [onSuccess, onError]
  );

  // Verify code
  const verifyCode = useCallback(
    async (channel: NotificationChannel, code: string) => {
      try {
        await notificationChannelsApi.verifyChannel({ channelId: channel.id, code });
        onSuccess?.('Email address verified successfully');
        await refreshChannels();
      } catch (error) {
        const message = error instanceof NotificationChannelApiError ? error.message : 'Verification failed';
        onError?.(message);
        throw error;
      }
    },
    [onSuccess, onError, refreshChannels]
  );

  // Add email channel
  const addEmailChannel = useCallback(
    async (name: string, email: string) => {
      try {
        await notificationChannelsApi.createEmailChannel({ name, email, emailType: 'notification' });
        onSuccess?.(`Email channel "${name}" added`);
        await refreshChannels();
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to add email channel');
      }
    },
    [onSuccess, onError, refreshChannels]
  );

  // Add digest channel
  const addDigestChannel = useCallback(
    async (name: string, email: string) => {
      try {
        await notificationChannelsApi.createEmailChannel({ name, email, emailType: 'digest' });
        onSuccess?.(`Digest recipient "${name}" added`);
        await refreshChannels();
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to add digest recipient');
      }
    },
    [onSuccess, onError, refreshChannels]
  );

  // Add Slack channel
  const addSlackChannel = useCallback(
    async (name: string, webhookUrl: string) => {
      try {
        await notificationChannelsApi.createSlackChannel({ name, webhookUrl });
        onSuccess?.(`Slack channel "${name}" added`);
        await refreshChannels();
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to add channel');
      }
    },
    [onSuccess, onError, refreshChannels]
  );

  // Add webhook channel
  const addWebhookChannel = useCallback(
    async (name: string, url: string, customHeaders?: Record<string, string>) => {
      try {
        await notificationChannelsApi.createWebhookChannel({ name, url, headers: customHeaders });
        onSuccess?.(`Webhook channel "${name}" added`);
        await refreshChannels();
      } catch (error) {
        onError?.(error instanceof NotificationChannelApiError ? error.message : 'Failed to add channel');
      }
    },
    [onSuccess, onError, refreshChannels]
  );

  return {
    channels,
    channelsByType,
    refreshChannels,
    testChannel,
    toggleChannel,
    deleteChannel,
    deleteRecipient,
    sendVerification,
    verifyCode,
    addEmailChannel,
    addDigestChannel,
    addSlackChannel,
    addWebhookChannel,
  };
}
